import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Users, 
  Home, 
  Wifi, 
  Car, 
  Coffee, 
  Tv, 
  Wind, 
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MapPin
} from 'lucide-react';
import { apartments } from '../data/mockData';

const ApartmentSection = () => {
  const [selectedApartment, setSelectedApartment] = useState(apartments[0]);
  const [selectedImageIndex, setSelectedImageIndex] = useState({});

  const handleApartmentChange = (apartmentId) => {
    const apartment = apartments.find(apt => apt.id === parseInt(apartmentId));
    setSelectedApartment(apartment);
  };

  const nextImage = (apartmentId) => {
    const apartment = apartments.find(apt => apt.id === apartmentId);
    setSelectedImageIndex(prev => ({
      ...prev,
      [apartmentId]: ((prev[apartmentId] || 0) + 1) % apartment.images.length
    }));
  };

  const prevImage = (apartmentId) => {
    const apartment = apartments.find(apt => apt.id === apartmentId);
    setSelectedImageIndex(prev => ({
      ...prev,
      [apartmentId]: ((prev[apartmentId] || 0) - 1 + apartment.images.length) % apartment.images.length
    }));
  };

  const getFeatureIcon = (feature) => {
    const iconMap = {
      'WiFi gratuit': Wifi,
      'Télévision': Tv,
      'Balcon': Wind,
      'Terrasse privée': Wind,
      'Cuisine équipée': Coffee,
      'Parking': Car,
      'default': CheckCircle
    };
    
    return iconMap[feature] || iconMap['default'];
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="appartements" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="px-4 py-2 text-sm font-medium bg-emerald-100 text-emerald-800 border-emerald-200 mb-4">
              <Home className="h-4 w-4 mr-2" />
              11 Appartements disponibles
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Nos 
              <span className="bg-gradient-to-r from-emerald-600 to-emerald-600 bg-clip-text text-transparent"> Appartements</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez notre sélection d'appartements entièrement équipés du F2 au F3, tous pensés pour votre confort.
            </p>
          </div>

          {/* Menu déroulant pour sélectionner l'appartement */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <select
                value={selectedApartment.id}
                onChange={(e) => handleApartmentChange(e.target.value)}
                className="appearance-none bg-white/95 backdrop-blur-sm border-3 border-emerald-300 rounded-2xl px-8 py-4 pr-12 text-lg font-semibold text-gray-900 focus:outline-none focus:border-emerald-600 focus:ring-4 focus:ring-emerald-200/50 transition-all duration-500 min-w-[350px] shadow-xl hover:shadow-warm-emerald-500/20"
              >
                {apartments.map((apartment) => (
                  <option key={apartment.id} value={apartment.id}>
                    {apartment.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-emerald-700 pointer-events-none" />
            </div>
          </div>

          {/* Affichage de l'appartement sélectionné */}
          <Card className="border-0 shadow-2xl bg-gradient-to-br from-white/95 to-emerald-50/30 backdrop-blur-sm">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-emerald-200 rounded-2xl overflow-hidden">
                {/* Image Gallery */}
                <div className="relative group">
                  <div className="aspect-[4/3] overflow-hidden rounded-l-lg">
                    <img 
                      src={selectedApartment.images[selectedImageIndex[selectedApartment.id] || 0]}
                      alt={selectedApartment.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    
                    {/* Navigation buttons */}
                    <button
                      onClick={() => prevImage(selectedApartment.id)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => nextImage(selectedApartment.id)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    
                    {/* Image indicators */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                      {selectedApartment.images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(prev => ({ ...prev, [selectedApartment.id]: index }))}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            (selectedImageIndex[selectedApartment.id] || 0) === index 
                              ? 'bg-white scale-125' 
                              : 'bg-white/60 hover:bg-white/80'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 lg:p-12 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-2">{selectedApartment.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                          <Badge variant="outline" className="border-emerald-200 text-emerald-700">
                            {selectedApartment.type}
                          </Badge>
                          <span className="flex items-center">
                            <Home className="h-4 w-4 mr-1" />
                            {selectedApartment.surface}
                          </span>
                          <span className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {selectedApartment.capacity}
                          </span>
                        </div>
                        {selectedApartment.floor && (
                          <div className="flex items-center text-sm text-emerald-700 font-medium">
                            <MapPin className="h-4 w-4 mr-1" />
                            {selectedApartment.floor}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-emerald-700 mb-1">
                          {selectedApartment.pricePerNight || selectedApartment.price}
                        </p>
                        {selectedApartment.priceCure && (
                          <p className="text-lg font-semibold text-gray-600">
                            {selectedApartment.priceCure}
                          </p>
                        )}
                      </div>
                    </div>

                    <p className="text-gray-700 text-lg leading-relaxed mb-6">
                      {selectedApartment.description}
                    </p>

                    <div className="mb-8">
                      <h4 className="font-semibold text-gray-900 mb-4">Équipements inclus :</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {selectedApartment.features.map((feature, index) => {
                          const IconComponent = getFeatureIcon(feature);
                          return (
                            <div key={index} className="flex items-center space-x-2">
                              <IconComponent className="h-4 w-4 text-emerald-600" />
                              <span className="text-gray-700 text-sm">{feature}</span>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Atouts particuliers supprimés sur demande utilisateur */}
                    </div>
                  </div>

                  <Button 
                    size="lg" 
                    className="w-full bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-800 hover:to-emerald-700 text-white font-semibold py-4 shadow-2xl hover:shadow-warm-emerald-500/30 transition-all duration-500 border-2 border-emerald-600 hover:border-warm-emerald-500 btn-hover-lift"
                    onClick={scrollToContact}
                  >
                    Réserver cet appartement
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ApartmentSection;